import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchUserAttributes } from "aws-amplify/auth";
import {
  KycQueueItem,
  KycDocumentType,
  DOCUMENT_TYPE_LABELS,
  PlatformKycAPI,
} from "../../services/platformKyc.service";
import PlatformAdminNav from "../../components/platform/PlatformAdminNav";
import PlatformAdminHeader from "../../components/platform/PlatformAdminHeader";

// KYC status filter — labels prefixed to read as a single "Status: …" control.
type StatusFilter = "submitted" | "verified" | "rejected" | "all";
const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "submitted", label: "Status: Pending" },
  { value: "verified",  label: "Status: Approved" },
  { value: "rejected",  label: "Status: Rejected" },
  { value: "all",       label: "Status: All" },
];

const DOC_TYPE_OPTIONS: { value: KycDocumentType | "all"; label: string }[] = [
  { value: "all", label: "All Doc Types" },
  { value: "medical_registration", label: "Medical Registration" },
  { value: "aadhar", label: "Aadhaar Card" },
  { value: "pan", label: "PAN Card" },
  { value: "degree", label: "Degree Certificate" },
  { value: "drug_license", label: "Drug License" },
  // Clinic institutional docs (Settings → Compliance & Verification)
  { value: "clinical_establishment_reg", label: "Clinical Establishment Certificate" },
  { value: "owner_aadhaar", label: "Director / Owner Aadhaar" },
  { value: "owner_pan", label: "Director / Owner PAN" },
  { value: "medical_director_nmc", label: "Medical Director's NMC Registration" },
];

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

const KycQueuePage: React.FC = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState<KycQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adminEmail, setAdminEmail] = useState<string>("");

  const [search, setSearch] = useState("");
  const [clinicFilter, setClinicFilter] = useState("all");
  const [docTypeFilter, setDocTypeFilter] = useState<KycDocumentType | "all">("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("submitted");

  useEffect(() => {
    let cancelled = false;
    // Fetch every status so the client-side "Status" dropdown can filter across
    // pending / approved / rejected without a refetch.
    Promise.all([PlatformKycAPI.listQueue("all"), fetchUserAttributes()])
      .then(([queue, attrs]) => {
        if (cancelled) return;
        setItems(queue);
        setAdminEmail(attrs.email ?? "");
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load KYC queue. Please refresh.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Unique clinics for dropdown — use clinic_name as key to avoid null clinic_id issues
  const clinicOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: { value: string; name: string }[] = [{ value: "all", name: "All Clinics" }];
    items.forEach(i => {
      const label = i.clinic_name ?? i.clinic_id ?? "";
      if (label && !seen.has(label)) {
        seen.add(label);
        opts.push({ value: label, name: label });
      }
    });
    return opts;
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return items.filter(i => {
      const clinicLabel = i.clinic_name ?? i.clinic_id ?? "";
      if (statusFilter !== "all" && i.status !== statusFilter) return false;
      if (clinicFilter !== "all" && clinicLabel !== clinicFilter) return false;
      if (docTypeFilter !== "all" && i.doc_type !== docTypeFilter) return false;
      if (q) {
        const clinicDisplay = (i.clinic_name ?? i.clinic_id).toLowerCase();
        return i.doctor_name.toLowerCase().includes(q) || clinicDisplay.includes(q);
      }
      return true;
    });
  }, [items, search, clinicFilter, docTypeFilter, statusFilter]);

  const pending = items.filter(i => i.status === "submitted").length;

  const handleRowClick = (item: KycQueueItem) => {
    navigate(`/platform/kyc/${item.kyc_doc_id}`, {
      state: { doctor_id: item.doctor_id, item },
    });
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <PlatformAdminNav adminEmail={adminEmail} />

      <div className="max-w-7xl mx-auto px-8 py-8">
        <PlatformAdminHeader subtitle="PlatformAdmin only — internal tool, low volume." />

        {loading && (
          <div className="text-sm text-slate-500 py-12 text-center">Loading queue…</div>
        )}

        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 px-5 py-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          /* Empty state */
          <div className="flex justify-center pt-16">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 px-10 py-10 text-center space-y-3 max-w-sm w-full">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <svg viewBox="0 0 24 24" className="w-6 h-6 text-green-600 fill-none stroke-current stroke-2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="font-semibold text-slate-800">No pending KYC submissions</p>
              <p className="text-sm text-slate-500">All doctor credentials are up to date.</p>
              <p className="text-xs text-slate-400">New submissions appear here automatically.</p>
              {adminEmail && (
                <div className="border-t border-slate-100 pt-3 mt-3">
                  <p className="text-xs text-slate-400">
                    Last reviewed: {new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} | {adminEmail}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <>
            {/* Pending count + status filter */}
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <span className={`inline-flex items-center rounded-full px-3 py-0.5 text-sm font-semibold ${
                pending === 0 ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-800"
              }`}>
                {pending} pending
              </span>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value as StatusFilter)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-[#0066ff]"
              >
                {STATUS_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <input
                type="text"
                placeholder="Search clinic or doctor…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-800 placeholder-slate-400 outline-none focus:ring-2 focus:ring-[#0066ff] w-56"
              />
              <select
                value={clinicFilter}
                onChange={e => setClinicFilter(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-[#0066ff]"
              >
                {clinicOptions.map(c => (
                  <option key={c.value} value={c.value}>{c.name}</option>
                ))}
              </select>
              <select
                value={docTypeFilter}
                onChange={e => setDocTypeFilter(e.target.value as KycDocumentType | "all")}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-[#0066ff]"
              >
                {DOC_TYPE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <p className="text-xs text-slate-500 mb-3">Sorted: Oldest submitted first</p>

            {/* Table */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide px-5 py-3">Clinic</th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide px-5 py-3">Doctor</th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide px-5 py-3">Document Type</th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide px-5 py-3">Submitted</th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide px-5 py-3">Status</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item, idx) => {
                    const isOldest = idx === 0 && !search && clinicFilter === "all" && docTypeFilter === "all" && statusFilter === "submitted";
                    return (
                      <tr
                        key={item.kyc_doc_id}
                        onClick={() => handleRowClick(item)}
                        className="border-b border-slate-50 last:border-0 hover:bg-slate-50 cursor-pointer transition-colors"
                      >
                        <td className="px-5 py-4 text-slate-700">
                          <div className="flex items-center gap-2">
                            {isOldest && (
                              <span className="rounded bg-orange-100 text-orange-700 text-xs font-semibold px-1.5 py-0.5">Oldest</span>
                            )}
                            {item.clinic_name ?? item.clinic_id}
                          </div>
                        </td>
                        <td className="px-5 py-4 text-slate-700">{item.doctor_name}</td>
                        <td className="px-5 py-4 text-slate-700">
                          {DOCUMENT_TYPE_LABELS[item.doc_type] ?? item.doc_type}
                        </td>
                        <td className="px-5 py-4 text-slate-500">{formatDate(item.submitted_at)}</td>
                        <td className="px-5 py-4">
                          <StatusBadge status={item.status} />
                        </td>
                        <td className="px-5 py-4 text-slate-400 text-right">›</td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-5 py-8 text-center text-sm text-slate-400">
                        No results match your filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <p className="text-xs text-slate-400 mt-3">
              Showing {filtered.length} of {items.length} — click any row to open review detail.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  if (status === "submitted") {
    return (
      <span className="inline-flex items-center rounded-full bg-yellow-50 border border-yellow-200 text-yellow-800 text-xs font-semibold px-2.5 py-0.5">
        Pending Review
      </span>
    );
  }
  if (status === "verified") {
    return (
      <span className="inline-flex items-center rounded-full bg-green-50 border border-green-200 text-green-700 text-xs font-semibold px-2.5 py-0.5">
        Verified
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-red-50 border border-red-200 text-red-700 text-xs font-semibold px-2.5 py-0.5">
      Rejected
    </span>
  );
};

export default KycQueuePage;
